import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, ValidationErrors } from '@angular/forms';
import {
  combineLatest,
  Observable,
  of,
  Subscription,
  distinctUntilChanged,
  defer,
  merge,
} from 'rxjs';
import { Store } from '@ngxs/store';
import { filter, switchMap, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { LoadAllCompetenceRolesForCurrentDepartment } from 'src/app/competence/state/competence-roles.actions';
import { ICompetenceRoleSimple } from 'src/app/competence/model/competence-role.model';
import { CompetenceRolesState } from 'src/app/competence/state/competence-roles.state';
import { IOptionGroup } from '../../models/option-group.model';
import { IdName } from '../../models/id-name';
import { IsStringArrayPipe } from '../../pipes/is-string-array.pipe';
import { JobPositionState } from '../../../competence/state/job-position.state';
import { JobPositionGroupState } from '../../../competence/state/job-position-group.state';
import {
  LoadAllJobPositions,
  LoadAllJobPositionsForDepartmentAndGroups,
} from '../../../competence/state/job-position.actions';
import {
  LoadAllJobPositionGroups,
  LoadAllJobPositionGroupsForDepartmentAndGroups,
} from '../../../competence/state/job-position-group.actions';
import { UIElementsModule } from '../../ui-elements/ui-elements.module';

type MultipleSelectionValue = string[] | IdName[];

// TODO Illia: with roles it becomes not only job position and group selector so rename is in order
@Component({
  selector: 'app-job-position-and-group-multiselect',
  templateUrl: './job-position-and-group-multiselect.component.html',
  providers: [IsStringArrayPipe],
  imports: [UIElementsModule, AsyncPipe],
})
export class JobPositionAndGroupMultiselectComponent<P extends string, G extends string>
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input() form: FormGroup & {
    [key in P]: FormControl<string | MultipleSelectionValue>;
  } & { [key in G]: FormControl<string | MultipleSelectionValue> };
  @Input() positionFieldName: P;
  @Input() positionGroupFieldName: G;
  @Input() rolesFieldName: string;
  @Input() label: string;
  @Input() required = false;
  @Input() description: string;
  @Input() positionsOnlyForSelectedDep = false;
  @Input() isMultiple = true;
  @Input() skipDispatch = false;
  @Input() disabled = false;
  @Input() selectedDepartments: string[];
  @Input() selectedDepartmentGroups: string[];

  control = new FormControl<any>(this.isMultiple ? [] : '', { nonNullable: true });

  getAllJobPositions$: Observable<IdName[]> = this.store.select(
    JobPositionState.getAllJobPositions
  );
  getAllJobPositionGroups$: Observable<IdName[]> = this.store.select(
    JobPositionGroupState.getAllJobPositionGroups
  );

  getAllRoles$: Observable<ICompetenceRoleSimple[]> = this.store.select(
    CompetenceRolesState.getAllRolesForCurrentDepartment
  );

  getAllJobPositionsForDepartment$: Observable<IdName[] | undefined> = this.store.select(
    JobPositionState.getJobPositionsForDepartmentAndGroups
  );
  getAllJobPositionGroupsForDepartment$: Observable<IdName[] | undefined> = this.store.select(
    JobPositionGroupState.getJobPositionGroupsForDepartmentAndGroups
  );
  options$: Observable<IOptionGroup[]>;

  get formPositionControl(): FormControl<string | MultipleSelectionValue> {
    return this.form[this.positionFieldName] as FormControl<string | MultipleSelectionValue>;
  }

  get formPositionGroupControl(): FormControl<string | MultipleSelectionValue> {
    return this.form[this.positionGroupFieldName] as FormControl<string | MultipleSelectionValue>;
  }

  get formRolesControl(): FormControl<string | MultipleSelectionValue> {
    return (this.form as any)[this.rolesFieldName];
  }

  private firstOptionGroup: IdName[];
  private secondOptionGroup: IdName[];
  private thirdOptionGroup: ICompetenceRoleSimple[];
  private subscriptions = new Subscription();

  constructor(
    private store: Store,
    private isStringArrayPipe: IsStringArrayPipe,
    private cdr: ChangeDetectorRef
  ) {
    this.subscriptions.add(this.onControlValueChange());
  }

  ngOnInit(): void {
    if (this.rolesFieldName && !this.formRolesControl) {
      console.error(
        `JOB POSITION AND GROUP MULTISELECT [${this.label}]: rolesFieldName is set but such field does not exist in the form`
      );
    }
    if (!this.selectedDepartments?.length && !this.selectedDepartmentGroups?.length) {
      this.options$ = this.initializeOptions().pipe(filter(this.isNotNull));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.form?.isFirstChange()) {
      this.handleFormStatus(this.form.status);
      this.subscriptions.add(this.onFormStatusChange());
    }
    if (changes.form?.currentValue) {
      this.setInitialValuesOnControl();
    }
    if (changes.required?.currentValue) {
      this.form.addValidators(this.validator);
    } else if (
      changes.required?.currentValue !== changes.required?.previousValue &&
      (this.form.validator || this.form.errors)
    ) {
      this.form.removeValidators(this.validator);
      this.form.setErrors(null);
      this.form.updateValueAndValidity();
    }
    if (
      changes.positionsOnlyForSelectedDep?.currentValue &&
      !changes.positionsOnlyForSelectedDep?.isFirstChange()
    ) {
      this.options$ = this.initializeOptions().pipe(filter(this.isNotNull));
    }
    if (
      changes.selectedDepartments?.currentValue ||
      changes.selectedDepartmentGroups?.currentValue
    ) {
      this.options$ = this.initializeOptions().pipe(filter(this.isNotNull));
    }
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(this.onPositionFieldsValueChange());
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onFormStatusChange(): Subscription {
    return this.form.statusChanges
      .pipe(
        distinctUntilChanged(),
        tap((status: FormControlStatus) => {
          this.handleFormStatus(status);
        }),
        tap(() => this.cdr.detectChanges())
      )
      .subscribe();
  }

  onControlValueChange(): Subscription {
    return this.control.valueChanges.subscribe((value) => {
      this.setItemsAndItemGroups(value);
    });
  }

  onPositionFieldsValueChange(): Subscription {
    const observables = [
      this.formPositionControl.valueChanges,
      this.formPositionGroupControl.valueChanges,
      ...(this.rolesFieldName ? [this.formRolesControl.valueChanges] : []),
    ];

    return merge(...observables).subscribe(() => {
      if (this.disabled || this.formPositionControl.disabled) {
        this.control.disable({ emitEvent: false });
      } else {
        this.control.enable({ emitEvent: false });
      }
      this.control.setValue(this.formatControlValues(), { emitEvent: false });
    });
  }

  handleFormStatus(status?: FormControlStatus): void {
    if ((status && status === 'DISABLED') || this.disabled) {
      this.control.disable({ emitEvent: false });
    } else if (!this.disabled) {
      this.control.enable({ emitEvent: false });
    }
  }

  markAsTouched(): void {
    this.control.markAsTouched();
  }

  private setItemsAndItemGroups(value: string | string[]) {
    const values = Array.isArray(value) ? value : [value];
    const item = values.filter((id) => this.firstOptionGroup?.some((element) => element.id === id));
    const itemGroup = values.filter((id) =>
      this.secondOptionGroup?.some((elementGroup) => elementGroup.id === id)
    );
    const roles = values.filter((id) =>
      this.thirdOptionGroup?.some((element) => element.id === id)
    );

    this.formPositionControl.setValue(this.isMultiple ? item : item[0], {
      emitEvent: false,
    });
    this.formPositionGroupControl.setValue(this.isMultiple ? itemGroup : itemGroup[0], {
      emitEvent: false,
    });
    if (this.rolesFieldName) {
      this.formRolesControl.setValue(this.isMultiple ? roles : roles[0], {
        emitEvent: false,
      });
    }
  }

  private initializeOptions(): Observable<IOptionGroup[]> {
    if (!this.skipDispatch) {
      const actions: any[] = [];
      if (this.positionsOnlyForSelectedDep) {
        actions.push(
          new LoadAllJobPositionsForDepartmentAndGroups(
            this.selectedDepartments,
            this.selectedDepartmentGroups
          ),
          new LoadAllJobPositionGroupsForDepartmentAndGroups(
            this.selectedDepartments,
            this.selectedDepartmentGroups
          )
        );
        if (this.rolesFieldName) {
          actions.push(new LoadAllCompetenceRolesForCurrentDepartment());
        }
      } else {
        actions.push(new LoadAllJobPositions(), new LoadAllJobPositionGroups());
        if (this.rolesFieldName) {
          actions.push(new LoadAllCompetenceRolesForCurrentDepartment());
        }
      }
      this.store.dispatch(actions);
    }
    const jobPositionsForAllDepartments$ = combineLatest([
      this.getAllJobPositions$,
      this.getAllJobPositionGroups$,
      this.getAllRoles$,
    ]);

    const jobPositionsForSelectedDepartment$ = combineLatest([
      this.getAllJobPositionsForDepartment$,
      this.getAllJobPositionGroupsForDepartment$,
      this.getAllRoles$,
    ]);

    return defer(() =>
      this.positionsOnlyForSelectedDep
        ? jobPositionsForSelectedDepartment$
        : jobPositionsForAllDepartments$
    ).pipe(
      switchMap(([elements, elementGroups, roles]) => {
        this.firstOptionGroup = elements ?? [];
        this.secondOptionGroup = elementGroups ?? [];
        this.thirdOptionGroup = roles ?? [];

        const optionGroups: IOptionGroup[] = [
          {
            type: this.positionFieldName,
            options: this.firstOptionGroup,
          },
          {
            type: this.positionGroupFieldName,
            options: this.secondOptionGroup,
          },
        ];

        if (this.rolesFieldName) {
          optionGroups.push({
            type: this.rolesFieldName,
            options: this.thirdOptionGroup,
          });
        }

        return of(optionGroups);
      }),
      tap(() => this.setItemsAndItemGroups(this.control.value))
    );
  }

  private setInitialValuesOnControl(): void {
    this.control.setValue(this.formatControlValues());
  }

  private formatControlValues(): string[] | MultipleSelectionValue | string {
    if (!this.isMultiple) {
      return (
        this.formPositionControl.value ||
        this.formPositionGroupControl.value ||
        (this.rolesFieldName ? this.formRolesControl.value : '') ||
        ''
      );
    }

    const chosenItemsAndGroups: string[] = [];
    if (this.formPositionControl.value !== null) {
      const value = this.formPositionControl.value as MultipleSelectionValue;
      if (this.isStringArray(value)) {
        value.forEach((id) => chosenItemsAndGroups.push(id));
      } else {
        value.forEach(({ id }) => chosenItemsAndGroups.push(id));
      }
    }
    if (this.formPositionGroupControl.value !== null) {
      const value = this.formPositionGroupControl.value as MultipleSelectionValue;
      if (this.isStringArray(value)) {
        value.forEach((id) => chosenItemsAndGroups.push(id));
      } else {
        value.forEach(({ id }) => chosenItemsAndGroups.push(id));
      }
    }

    if (this.rolesFieldName && this.formRolesControl.value !== null) {
      const value = this.formRolesControl.value as MultipleSelectionValue;
      if (this.isStringArray(value)) {
        value.forEach((id) => chosenItemsAndGroups.push(id));
      } else {
        value.forEach(({ id }) => chosenItemsAndGroups.push(id));
      }
    }
    return chosenItemsAndGroups;
  }

  private isStringArray(array: MultipleSelectionValue): array is string[] {
    return this.isStringArrayPipe.transform(array);
  }

  private isNotNull<T>(value: T): value is NonNullable<T> {
    return value !== null && value !== undefined;
  }

  private validator = (): ValidationErrors | null => {
    const positionExists = this.formPositionControl.value?.length;
    const positionGroupExists = this.formPositionGroupControl.value?.length;
    const rolesExists = this.rolesFieldName ? this.formRolesControl.value?.length : true;

    return !positionExists && !positionGroupExists && !rolesExists
      ? { jobPositionOrJobPositionGroupRequired: true }
      : null;
  };
}
